import { type ReactNode, useEffect, useRef, useState } from 'react';
import { audio, type Player, type PlaySpec } from '../core/audio';
import { Icon, type IconName } from './icons';
import { Rich, richText } from './Rich';

export type BtnKind = 'primary' | 'ghost' | 'soft' | 'subtle' | 'danger';

export interface BtnProps {
  kind?: BtnKind;
  size?: 'lg' | 'sm';
  icon?: IconName;
  className?: string;
  title?: string;
  ariaLabel?: string;
  disabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}

export function Btn({
  kind = 'ghost',
  size,
  icon,
  className,
  title,
  ariaLabel,
  disabled,
  hidden,
  onClick,
  children,
}: BtnProps) {
  const cls = `btn btn-${kind}${size ? ` btn-${size}` : ''}${className ? ` ${className}` : ''}`;
  return (
    <button
      type="button"
      className={cls}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
      hidden={hidden}
      onClick={onClick}
    >
      {icon ? <Icon name={icon} /> : null}
      {children != null && children !== '' ? <span>{children}</span> : null}
    </button>
  );
}

export function IconBtn({
  icon,
  label,
  onClick,
  className,
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-btn${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  );
}

export interface PlayButtonProps {
  label?: string;
  /** Starts playback and returns the player; the button toggles to "stop" until it ends. */
  start: () => Player | null | undefined;
  kind?: BtnKind;
  className?: string;
  /** Format the label with the inline formatter (default true). */
  rich?: boolean;
  /** What the button plays, for screen readers, when it has no visible label (an icon in a table row). */
  name?: string;
}

/** Play/stop toggle for any player factory. */
export function PlayButton({ label, start, kind = 'soft', className, rich = true, name }: PlayButtonProps) {
  const [playing, setPlaying] = useState(false);
  const player = useRef<Player | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const text = label ? (rich ? richText(label) : label) : '';
  const onClick = async () => {
    const p = player.current;
    if (p && !p.stopped && !p.finished) {
      p.stop();
      return;
    }
    audio.stopAll();
    await audio.resume();
    const next = start();
    if (!next) return;
    player.current = next;
    setPlaying(true);
    next.done.then(() => {
      if (alive.current && player.current === next) setPlaying(false);
    });
  };
  return (
    <button
      type="button"
      className={`btn play-btn btn-${kind}${className ? ` ${className}` : ''}${playing ? ' is-playing' : ''}`}
      aria-label={`${playing ? 'Durdur' : 'Çal'}${text || name ? `: ${text || name}` : ''}`}
      onClick={onClick}
    >
      <Icon name={playing ? 'stop' : 'play'} />
      {label ? <span>{rich ? <Rich text={label} inert /> : label}</span> : null}
    </button>
  );
}

export function SpecButton({
  label,
  spec,
  kind,
  className,
}: {
  label: string;
  spec: PlaySpec;
  kind?: BtnKind;
  className?: string;
}) {
  return <PlayButton label={label} start={() => audio.playSpec(spec)} kind={kind} className={className} />;
}
